<template>
  <v-card class="notification-center" :class="roleClass" elevation="1">
    <!-- 🎯 Header -->
    <v-card-title class="d-flex align-center justify-space-between pa-3">
      <div class="d-flex align-center">
        <v-badge
          :content="unreadCount"
          :model-value="unreadCount > 0"
          color="error"
          offset-x="8"
          offset-y="8"
        >
          <v-icon icon="mdi-bell" color="primary" class="me-2" size="20" />
        </v-badge>
        <h4 class="text-subtitle-1 mb-0">Centro de Notificaciones</h4>
      </div>
      
      <!-- 🔧 Controls -->
      <div class="d-flex align-center gap-2">
        <!-- Filter by type -->
        <v-select
          v-model="filterType"
          :items="notificationTypes"
          item-title="label"
          item-value="value"
          variant="outlined"
          density="compact"
          hide-details
          style="width: 120px"
        />

        <!-- Mark all as read -->
        <v-btn
          icon="mdi-email-mark-as-unread"
          variant="text"
          size="small"
          @click="markAllAsRead"
          :disabled="true"
          title="Acción local no persistida en backend"
        />

        <!--
          P13-V7B (refutation) · RETIRADO el engranaje "Configurar Notificaciones". Su único ítem
          emitía 'settingsOpen', y ninguno de los montajes vivos (NotificationBell.vue y
          NotificationsView.vue) lo escucha, ni existe una pantalla de preferencias de notificaciones
          detrás: el engranaje abría un menú cuyo único control no hacía nada. Con él se va el menú.

          P13-V5A-R5 · RETIRADO el ítem "Exportar Historial" (icono mdi-download) del mismo menú.
          Prometía una exportación del historial de notificaciones: al pulsarlo emitía
          'exportRequest', y ese evento NO tiene ni un solo listener en todo el frontend
          (los tres montajes de NotificationCenter — NotificationsView.vue,
          RoleDemoView.vue y notifications/NotificationBell.vue — no lo escuchan), ni
          existe endpoint de exportación de notificaciones en el backend.
          No se implementa la exportación: se retira la promesa. La exportación real y
          autorizada que sí existe es la de reportes organizacionales
          (/api/reports/organizational/export/{excel,pdf}/:reportType), accesible en
          Reportes → Organizacionales.
        -->
      </div>
    </v-card-title>

    <v-alert
      v-if="degradedMessage"
      :type="syncError ? 'warning' : 'info'"
      variant="tonal"
      density="compact"
      class="mx-3 mb-3"
    >
      {{ degradedMessage }}
    </v-alert>

    <div class="d-flex align-center flex-wrap ga-2 px-3 pb-3">
      <v-chip :color="statusColor" size="small" variant="tonal">
        {{ statusLabel }}
      </v-chip>
      <v-chip v-if="lastSyncAt" size="small" variant="outlined">
        Sync {{ formatTime(new Date(lastSyncAt)) }}
      </v-chip>
      <v-chip v-if="localActionNotice" color="warning" size="small" variant="tonal">
        {{ localActionNotice }}
      </v-chip>
    </div>

    <!-- 📋 Notifications List -->
    <v-card-text class="pa-0">
      <!-- Quick Filters -->
      <div class="notification-filters pa-3 pb-0">
        <v-chip-group v-model="selectedFilter" column>
          <v-chip
            v-for="filter in quickFilters"
            :key="filter.value"
            :value="filter.value"
            size="small"
            variant="outlined"
            filter
          >
            <v-icon :icon="filter.icon" size="14" class="me-1" />
            {{ filter.label }}
            <v-badge
              v-if="filter.count > 0"
              :content="filter.count"
              color="primary"
              inline
              class="ms-1"
            />
          </v-chip>
        </v-chip-group>
      </div>

      <!-- Notifications Container -->
      <div class="notifications-container">
        <v-virtual-scroll
          :items="filteredNotifications"
          :item-height="80"
          height="400"
        >
          <template #default="{ item }">
            <div
              class="notification-item"
              :class="{
                'notification-unread': !item.read,
                'notification-priority': item.priority === 'high',
                'notification-urgent': item.priority === 'urgent'
              }"
              @click="selectNotification(item)"
            >
              <!-- Notification Icon -->
              <div class="notification-icon">
                <v-avatar
                  :color="getNotificationColor(item.type)"
                  size="32"
                >
                  <v-icon
                    :icon="getNotificationIcon(item.type)"
                    color="white"
                    size="16"
                  />
                </v-avatar>
              </div>

              <!-- Notification Content -->
              <div class="notification-content">
                <div class="notification-header">
                  <span class="notification-title">{{ item.title }}</span>
                  <span class="notification-time">{{ formatTime(item.timestamp) }}</span>
                </div>
                
                <div class="notification-message">{{ item.message }}</div>
                
                <div class="notification-meta">
                  <v-chip
                    :color="getPriorityColor(item.priority)"
                    size="x-small"
                    variant="flat"
                  >
                    {{ getPriorityLabel(item.priority) }}
                  </v-chip>
                  
                  <v-chip
                    v-if="item.category"
                    size="x-small"
                    variant="outlined"
                    class="ms-1"
                  >
                    {{ item.category }}
                  </v-chip>

                  <v-chip
                    v-if="item.origin === 'cache'"
                    color="warning"
                    size="x-small"
                    variant="tonal"
                    class="ms-1"
                  >
                    cache
                  </v-chip>

                  <v-chip
                    v-else-if="!item.persisted"
                    color="grey"
                    size="x-small"
                    variant="tonal"
                    class="ms-1"
                  >
                    local
                  </v-chip>

                  <v-chip
                    v-if="item.pendingReadSync"
                    color="info"
                    size="x-small"
                    variant="tonal"
                    class="ms-1"
                  >
                    guardando
                  </v-chip>

                  <v-chip
                    v-if="item.syncError"
                    color="error"
                    size="x-small"
                    variant="tonal"
                    class="ms-1"
                  >
                    error
                  </v-chip>
                </div>
              </div>

              <!-- Notification Actions -->
              <div class="notification-actions">
                <v-btn
                  v-if="!item.read"
                  icon="mdi-email-mark-as-unread"
                  variant="text"
                  size="x-small"
                  :loading="item.pendingReadSync"
                  @click.stop="markAsRead(item.id)"
                />
                
                <v-menu>
                  <template #activator="{ props }">
                    <v-btn
                      icon="mdi-dots-vertical"
                      variant="text"
                      size="x-small"
                      v-bind="props"
                      @click.stop
                    />
                  </template>
                  <v-list>
                    <v-list-item @click="markAsRead(item.id)" v-if="!item.read">
                      <template #prepend>
                        <v-icon icon="mdi-email-open" />
                      </template>
                      <v-list-item-title>Marcar como leída</v-list-item-title>
                    </v-list-item>
                    <v-list-item disabled>
                      <template #prepend>
                        <v-icon :icon="item.pinned ? 'mdi-pin-off' : 'mdi-pin'" />
                      </template>
                      <v-list-item-title>
                        {{ item.pinned ? 'Desfijar' : 'Fijar' }}
                      </v-list-item-title>
                      <v-list-item-subtitle>No persistido en backend</v-list-item-subtitle>
                    </v-list-item>
                    <v-list-item disabled>
                      <template #prepend>
                        <v-icon icon="mdi-archive" />
                      </template>
                      <v-list-item-title>Archivar</v-list-item-title>
                      <v-list-item-subtitle>No persistido en backend</v-list-item-subtitle>
                    </v-list-item>
                    <v-list-item disabled>
                      <template #prepend>
                        <v-icon icon="mdi-delete" />
                      </template>
                      <v-list-item-title>Eliminar</v-list-item-title>
                      <v-list-item-subtitle>No persistido en backend</v-list-item-subtitle>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </div>
            </div>
          </template>
        </v-virtual-scroll>

        <!-- Empty State -->
        <div v-if="filteredNotifications.length === 0" class="empty-state">
          <v-icon icon="mdi-bell-off" size="48" color="grey-lighten-1" />
          <p class="text-caption mt-2">No hay notificaciones</p>
        </div>
      </div>
    </v-card-text>

    <!-- 🎯 Notification Detail Panel -->
    <v-expand-transition>
      <div v-if="selectedNotificationDetail" class="notification-detail">
        <v-divider />
        <v-card-text class="pa-3">
          <div class="d-flex align-center justify-space-between mb-3">
            <h6 class="text-subtitle-2">{{ selectedNotificationDetail.title }}</h6>
            <v-btn
              icon="mdi-close"
              variant="text"
              size="small"
              @click="selectedNotificationDetail = null"
            />
          </div>
          
          <div class="notification-detail-content">
            <div class="notification-full-message mb-3">
              {{ selectedNotificationDetail.fullMessage || selectedNotificationDetail.message }}
            </div>
            
            <div class="notification-detail-meta mb-3">
              <v-row dense>
                <v-col cols="6">
                  <div class="text-caption text-medium-emphasis">Fecha:</div>
                  <div>{{ formatFullDate(selectedNotificationDetail.timestamp) }}</div>
                </v-col>
                <v-col cols="6">
                  <div class="text-caption text-medium-emphasis">Origen:</div>
                  <div>{{ selectedNotificationDetail.source || 'Sistema' }}</div>
                </v-col>
              </v-row>
            </div>
            
            <!-- P13-V7B (refutation): the per-notification action buttons are RETIRED. Each one only
                 emitted 'notificationAction' (and logged); no live mount listens to it and the store
                 never produces an `actions` list, so a button could only ever do nothing. -->
          </div>
        </v-card-text>
      </div>
    </v-expand-transition>

    <!-- 📊 Notification Statistics (Role-based) -->
    <v-card-actions v-if="showStatistics" class="pa-3">
      <v-row dense>
        <v-col v-for="stat in notificationStats" :key="stat.label" cols="auto">
          <div class="text-center">
            <div class="text-caption text-medium-emphasis">{{ stat.label }}</div>
            <div class="text-subtitle-2 font-weight-bold">{{ stat.value }}</div>
          </div>
        </v-col>
      </v-row>
    </v-card-actions>
  </v-card>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useMainStore } from '@/stores/main'
import { useNotificationsStore } from '@/stores/notifications'
import { storeToRefs } from 'pinia'

// 🎯 Types
interface Notification {
  id: string
  title: string
  message: string
  fullMessage?: string
  type: 'info' | 'warning' | 'error' | 'success' | 'system'
  priority: 'low' | 'medium' | 'high' | 'urgent'
  category?: string
  source?: string
  timestamp: Date
  read: boolean
  pinned: boolean
  archived: boolean
  origin?: 'backend' | 'realtime' | 'cache' | 'local'
  persisted?: boolean
  pendingReadSync?: boolean
  syncError?: string | null
  localOnlyReason?: string | null
  // P13-V7B (refutation): `actions` removed with the buttons that rendered it (see the template).
}

interface NotificationFilter {
  label: string
  value: string
  icon: string
  count: number
}

interface NotificationStat {
  label: string
  value: string | number
}

// 📝 Props
interface Props {
  showStatistics?: boolean
  maxNotifications?: number
  autoRefresh?: boolean
  enableRealTime?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  showStatistics: false,
  maxNotifications: 100,
  autoRefresh: true,
  enableRealTime: false
})

// 🎯 Emits
const emit = defineEmits<{
  notificationSelect: [notification: Notification]
  // P13-V7B (refutation): 'notificationAction' and 'settingsOpen' eliminados junto con los controles
  // que los emitían — ningún montaje vivo los escuchaba.
  // P13-V5A-R5: 'exportRequest' eliminado junto con el ítem "Exportar Historial" del menú.
  // Nadie lo emitía ya y nadie lo escuchaba nunca.
}>()

// 🔧 Stores
const store = useMainStore()
const notifStore = useNotificationsStore()
const {
  items: storeItems,
  syncing,
  syncError,
  usingCachedData,
  realtimeConnected,
  lastSyncAt,
  localActionNotice,
  realtimeOnly
} = storeToRefs(notifStore)

// 🔧 Reactive state
const filterType = ref('all')
const selectedFilter = ref('all')
const selectedNotificationDetail = ref<Notification | null>(null)

// 📋 Notifications data: enlazar con store
const notifications = computed<Notification[]>(() =>
  storeItems.value.map((n: any) => ({
    id: String(n.id),
    title: n.title,
    message: n.message,
    fullMessage: n.fullMessage,
    type: n.type || 'system',
    priority: n.priority || 'medium',
    category: n.category,
    source: n.source,
    timestamp: n.timestamp instanceof Date ? n.timestamp : new Date(n.timestamp),
    read: !!n.read,
    pinned: !!n.pinned,
    archived: !!n.archived,
    origin: n.origin,
    persisted: n.persisted ?? notifStore.isBackendBackedId(String(n.id)),
    pendingReadSync: !!n.pendingReadSync,
    syncError: n.syncError,
    localOnlyReason: n.localOnlyReason
  }))
)


// 🎨 Computed properties
const roleClass = computed(() => {
  const role = store.currentUser?.role?.toLowerCase().replace(/\s+/g, '-')
  return `notification-center-${role}`
})

const unreadCount = computed(() => 
  notifications.value.filter(n => !n.read && !n.archived).length
)

const notificationTypes = computed(() => [
  { label: 'Todas', value: 'all' },
  { label: 'No leídas', value: 'unread' },
  { label: 'Importantes', value: 'important' },
  { label: 'Archivadas', value: 'archived' }
])

const quickFilters = computed((): NotificationFilter[] => [
  {
    label: 'Todas',
    value: 'all',
    icon: 'mdi-bell',
    count: notifications.value.filter(n => !n.archived).length
  },
  {
    label: 'Urgentes',
    value: 'urgent',
    icon: 'mdi-alert',
    count: notifications.value.filter(n => n.priority === 'urgent' && !n.archived).length
  },
  {
    label: 'Operaciones',
    value: 'operations',
    icon: 'mdi-cog',
    count: notifications.value.filter(n => n.category === 'Operaciones' && !n.archived).length
  },
  {
    label: 'Mantenimiento',
    value: 'maintenance',
    icon: 'mdi-wrench',
    count: notifications.value.filter(n => n.category === 'Mantenimiento' && !n.archived).length
  },
  {
    label: 'Sistema',
    value: 'system',
    icon: 'mdi-server',
    count: notifications.value.filter(n => n.type === 'system' && !n.archived).length
  }
])

const filteredNotifications = computed(() => {
  let filtered = notifications.value.filter(n => !n.archived)
  
  // Apply type filter
  if (filterType.value === 'unread') {
    filtered = filtered.filter(n => !n.read)
  } else if (filterType.value === 'important') {
    filtered = filtered.filter(n => n.priority === 'high' || n.priority === 'urgent' || n.pinned)
  } else if (filterType.value === 'archived') {
    filtered = notifications.value.filter(n => n.archived)
  }
  
  // Apply quick filter
  if (selectedFilter.value && selectedFilter.value !== 'all') {
    if (selectedFilter.value === 'urgent') {
      filtered = filtered.filter(n => n.priority === 'urgent')
    } else if (selectedFilter.value === 'operations') {
      filtered = filtered.filter(n => n.category === 'Operaciones')
    } else if (selectedFilter.value === 'maintenance') {
      filtered = filtered.filter(n => n.category === 'Mantenimiento')
    } else if (selectedFilter.value === 'system') {
      filtered = filtered.filter(n => n.type === 'system')
    }
  }
  
  // Sort by priority and timestamp
  return filtered.sort((a, b) => {
    // Pinned first
    if (a.pinned && !b.pinned) return -1
    if (!a.pinned && b.pinned) return 1
    
    // Then by priority
    const priorityOrder = { urgent: 4, high: 3, medium: 2, low: 1 }
    const aPriority = priorityOrder[a.priority]
    const bPriority = priorityOrder[b.priority]
    
    if (aPriority !== bPriority) return bPriority - aPriority
    
    // Finally by timestamp (newest first)
    return b.timestamp.getTime() - a.timestamp.getTime()
  })
})

const notificationStats = computed((): NotificationStat[] => {
  const role = store.currentUser?.role
  
  if (!props.showStatistics || ['Operador de Rutas', 'Empleado de Agencia'].includes(role || '')) {
    return []
  }
  
  return [
    { label: 'Total', value: notifications.value.length },
    { label: 'No leídas', value: unreadCount.value },
    { label: 'Urgentes', value: notifications.value.filter(n => n.priority === 'urgent').length },
    { label: 'Esta semana', value: notifications.value.filter(n => 
      n.timestamp.getTime() > Date.now() - 7 * 24 * 60 * 60 * 1000
    ).length }
  ]
})

// WS4 · H2: for an identity the backend does not admit to the durable reader the centre is realtime-only —
// no sync state to report (see services/notifications.admission.ts)
const statusLabel = computed(() => {
  if (realtimeOnly.value) return realtimeConnected.value ? 'tiempo real' : 'sin realtime'
  if (syncing.value) return 'sincronizando'
  if (syncError.value) return 'degradado'
  if (usingCachedData.value) return 'cache'
  if (!realtimeConnected.value) return 'sin realtime'
  return 'sincronizado'
})

const statusColor = computed(() => {
  if (realtimeOnly.value) return realtimeConnected.value ? 'success' : 'orange'
  if (syncing.value) return 'info'
  if (syncError.value) return 'warning'
  if (usingCachedData.value || !realtimeConnected.value) return 'orange'
  return 'success'
})

const degradedMessage = computed(() => {
  if (realtimeOnly.value) return realtimeConnected.value ? '' : 'Realtime no conectado; puede haber demoras en nuevas notificaciones.'
  if (syncError.value) return `No se pudo sincronizar con backend: ${syncError.value}`
  if (usingCachedData.value) return 'Mostrando cache local hasta recuperar sincronización con backend.'
  if (!realtimeConnected.value) return 'Realtime no conectado; puede haber demoras en nuevas notificaciones.'
  return ''
})

// 🎯 Methods
const getNotificationIcon = (type: string): string => {
  switch (type) {
    case 'success': return 'mdi-check-circle'
    case 'warning': return 'mdi-alert'
    case 'error': return 'mdi-alert-circle'
    case 'system': return 'mdi-cog'
    default: return 'mdi-information'
  }
}

const getNotificationColor = (type: string): string => {
  switch (type) {
    case 'success': return 'success'
    case 'warning': return 'warning'
    case 'error': return 'error'
    case 'system': return 'purple'
    default: return 'info'
  }
}

const getPriorityLabel = (priority: string): string => {
  switch (priority) {
    case 'urgent': return 'Urgente'
    case 'high': return 'Alta'
    case 'medium': return 'Media'
    case 'low': return 'Baja'
    default: return priority
  }
}

const getPriorityColor = (priority: string): string => {
  switch (priority) {
    case 'urgent': return 'error'
    case 'high': return 'warning'
    case 'medium': return 'info'
    case 'low': return 'success'
    default: return 'default'
  }
}

const formatTime = (timestamp: Date): string => {
  const now = new Date()
  const diff = now.getTime() - timestamp.getTime()
  const minutes = Math.floor(diff / (1000 * 60))
  const hours = Math.floor(minutes / 60)
  const days = Math.floor(hours / 24)
  
  if (days > 0) return `${days}d`
  if (hours > 0) return `${hours}h`
  if (minutes > 0) return `${minutes}m`
  return 'Ahora'
}

const formatFullDate = (timestamp: Date): string => {
  return timestamp.toLocaleString('es-ES', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

const selectNotification = (notification: Notification) => {
  selectedNotificationDetail.value = notification
  if (!notification.read) {
    void markAsRead(notification.id)
  }
  emit('notificationSelect', notification)
}

const markAsRead = async (id: string) => {
  await notifStore.markAsReadAndSync(id)
}

const markAllAsRead = () => {
  notifStore.markAllAsRead()
}

const pinNotification = (id: string) => {
  notifStore.togglePin(id)
}

const archiveNotification = (id: string) => {
  notifStore.archive(id)
  selectedNotificationDetail.value = null
}

const deleteNotification = (id: string) => {
  notifStore.remove(id)
  selectedNotificationDetail.value = null
}

// P13-V7B (refutation): `executeNotificationAction` and `openNotificationSettings` were REMOVED. Their
// whole bodies emitted events no live mount listens to (the first also logged): inert controls.

// P13-V5A-R5: 'exportNotifications' retirado. Su único cuerpo era emit('exportRequest'), un
// evento sin ningún listener en el frontend y sin endpoint de exportación de notificaciones
// detrás. Un handler que sólo emite al vacío es un control inerte, no una capacidad.

// 🔄 Lifecycle
onMounted(() => {
  if (props.enableRealTime) {
    void notifStore.initialize()
  }
})
</script>

<style scoped>
.notification-center {
  border-radius: 12px;
}

.notification-filters {
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

.notifications-container {
  position: relative;
}

.notification-item {
  display: flex;
  padding: 12px 16px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  cursor: pointer;
  transition: all 0.2s ease;
}

.notification-item:hover {
  background-color: rgba(0, 0, 0, 0.02);
}

.notification-unread {
  background-color: rgba(33, 150, 243, 0.02);
  border-left: 3px solid #2196f3;
}

.notification-priority {
  border-left: 3px solid #ff9800;
}

.notification-urgent {
  border-left: 3px solid #f44336;
  background-color: rgba(244, 67, 54, 0.02);
}

.notification-icon {
  margin-right: 12px;
}

.notification-content {
  flex: 1;
  min-width: 0;
}

.notification-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
}

.notification-title {
  font-weight: 500;
  font-size: 0.875rem;
  line-height: 1.2;
}

.notification-time {
  font-size: 0.75rem;
  color: rgba(0, 0, 0, 0.6);
  margin-left: 8px;
}

.notification-message {
  font-size: 0.8rem;
  color: rgba(0, 0, 0, 0.7);
  line-height: 1.3;
  margin-bottom: 8px;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.notification-meta {
  display: flex;
  align-items: center;
  gap: 4px;
}

.notification-actions {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-left: 8px;
}

.notification-detail {
  background-color: rgba(0, 0, 0, 0.01);
}

.notification-detail-content {
  font-size: 0.875rem;
  line-height: 1.5;
}

.notification-full-message {
  color: rgba(0, 0, 0, 0.8);
}

.notification-detail-meta {
  background-color: rgba(0, 0, 0, 0.02);
  border-radius: 6px;
  padding: 12px;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  color: rgba(0, 0, 0, 0.5);
}

/* Role-specific styling */
.notification-center-operador-de-rutas {
  border-left: 4px solid #ff9800;
}

.notification-center-supervisor-de-zona {
  border-left: 4px solid #2196f3;
}

.notification-center-gerente-regional {
  border-left: 4px solid #9c27b0;
}

.notification-center-analista-central {
  border-left: 4px solid #607d8b;
}

.notification-center-super-admin {
  border-left: 4px solid #f44336;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .notification-item {
    padding: 8px 12px;
  }
  
  .notification-header {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .notification-time {
    margin-left: 0;
    margin-top: 2px;
  }
  
  .notification-actions {
    flex-direction: row;
  }
}

/* Animation for new notifications */
@keyframes slideIn {
  from {
    transform: translateX(-100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

.notification-item.new {
  animation: slideIn 0.3s ease-out;
}
</style>
